/**
 * Konfigurasi Theme Ant Design - Booku v2
 *
 * File ini mengkonfigurasi tema Ant Design menggunakan
 * warna dari sistem warna terpusat (colors.ts)
 */

import type { ThemeConfig } from 'antd';
import { colors } from './colors';

/**
 * Booku V2 — Compact Density Theme
 *
 * Tema Ant Design diatur agar information-dense untuk PC layar kecil
 * (target baseline 1366×768).
 *
 * @see .claude/docs/Booku v2/ui-patterns/compact-density-pattern.md
 */
export const antdTheme: ThemeConfig = {
  token: {
    // Primary
    colorPrimary: colors.primary,
    colorPrimaryHover: colors.primaryHover,
    colorPrimaryActive: colors.primaryDark,
    colorPrimaryBg: colors.primarySurface,

    // Success
    colorSuccess: colors.success,
    colorSuccessBg: colors.successSurface,

    // Warning
    colorWarning: colors.warning,
    colorWarningBg: colors.warningSurface,

    // Error
    colorError: colors.error,
    colorErrorBg: colors.errorSurface,

    // Info
    colorInfo: colors.info,
    colorInfoBg: colors.infoSurface,

    // Text
    colorText: colors.textPrimary,
    colorTextSecondary: colors.textSecondary,
    colorTextDisabled: colors.textDisabled,

    // Background
    colorBgContainer: colors.bgPrimary,
    colorBgLayout: colors.bgSecondary,

    // Border
    colorBorder: colors.border,
    colorBorderSecondary: colors.borderLight,

    // === Compact Density ===
    fontSize: 12,                  // dari default 14
    fontSizeSM: 11,
    fontSizeLG: 13,
    controlHeight: 28,             // dari default 32
    controlHeightSM: 22,           // dari default 24
    controlHeightLG: 36,
    paddingContentVertical: 6,
    paddingContentHorizontal: 10,
    padding: 12,                   // dari default 16
    paddingSM: 8,
    paddingXS: 4,
    margin: 12,
    marginSM: 8,
    marginXS: 4,
    lineHeight: 1.4,

    borderRadius: 4,               // dari 6
    borderRadiusSM: 3,
    borderRadiusLG: 6,
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  components: {
    Button: {
      primaryShadow: 'none',
      paddingInline: 10,
      paddingInlineSM: 6,
      controlHeight: 28,
      controlHeightSM: 22,
    },
    Input: {
      controlHeight: 28,
      paddingInline: 8,
    },
    InputNumber: {
      controlHeight: 28,
      paddingInline: 8,
    },
    Select: {
      controlHeight: 28,
    },
    DatePicker: {
      controlHeight: 28,
      paddingInline: 8,
    },
    Table: {
      cellPaddingBlock: 4,         // padding atas-bawah cell (default ~12)
      cellPaddingInline: 8,        // padding kiri-kanan cell (default 16)
      cellPaddingBlockSM: 3,
      cellPaddingInlineSM: 6,
      headerBg: '#FAFAFA',
      headerSplitColor: '#E0E0E0',
      fontSize: 11,                // Tabel pakai font lebih kecil dari base (12)
                                   // untuk meningkatkan density data per layar.
                                   // Disesuaikan 11-05-2026.
      headerColor: '#546E55',
      borderColor: '#EEEEEE',
    },
    Form: {
      itemMarginBottom: 10,        // dari default 24
      verticalLabelPadding: '0 0 2px',
      labelFontSize: 12,
    },
    Card: {
      boxShadowTertiary: '0 1px 2px rgba(0, 0, 0, 0.05)',
      paddingLG: 12,
      padding: 10,
      headerHeight: 36,
      headerHeightSM: 30,
    },
    Modal: {
      titleFontSize: 14,
      paddingContentHorizontal: 16,
    },
    Tag: {
      defaultBg: '#FAFAFA',
    },
    Tabs: {
      horizontalItemPadding: '6px 12px',
      titleFontSize: 12,
    },
    Tooltip: {
      fontSize: 11,
      paddingXS: 6,
    },
    Dropdown: {
      paddingBlock: 4,
      controlPaddingHorizontal: 10,
    },
    Pagination: {
      itemSize: 24,
    },
  },
};
